import * as React from 'react'
import * as Tooltip from 'react-tooltip'
import moment from 'moment'
import Select from 'react-select'
import { inject, observer } from 'mobx-react'
import { toJS } from 'mobx'
import { CreateCustomer, ICustomer, Gender, Province } from '@getgreenline/homi-shared'
import Datepicker from 'react-datepicker'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import './CustomerInfoForm.scss'
import { SecureImage } from '../../containers/SecureImage'
import { SecureImageUploadModal } from '../SecureImageUploadModal'
import { FormError } from '../FormError'
import { parseErrorMsg, isPhoneNumberValid, isEmailValid, GrRegex } from '../../utilities/helpers'
import { barcodeStore } from '../BarcodeStore'
import classNames from 'classnames'
import { CustomError } from '@getgreenline/shared'
import { BlazeButton } from '../CustomButtons/BlazeButton'
import { CustomersApi } from '@getgreenline/customers'
import { openCustomerMFEModal } from '../../mfeEvents/customer'

interface IProps {
  currentCompanyStore?: CurrentCompanyStore
  canSaveCustomer: boolean
  customer: CreateCustomer
  onSubmit: (customer: ICustomer) => void
  onCustomerUndelete?: () => void
}

interface IState {
  showIdImageUploadModal: boolean
  showProfileImageUploadModal: boolean
  isSaving: boolean
  errorMessage: string
  customerIdToUndelete: string | undefined
}

@inject('currentCompanyStore')
@observer
export class CustomerInfoForm extends React.Component<IProps, IState> {
  CARD_ID = 'customer-info-form-card-id'
  PLACE_HOLDER_ID = 'placeholder-id'

  constructor(props: IProps) {
    super(props)
    this.state = {
      showIdImageUploadModal: false,
      showProfileImageUploadModal: false,
      isSaving: false,
      errorMessage: '',
      customerIdToUndelete: undefined,
    }
  }

  customerLocationIdValue = (customer: CreateCustomer) => {
    const locations = this.props.currentCompanyStore!.locations || []
    const location = locations.find((location) => location.id === customer.locationId)

    return location?.id || null
  }

  componentDidMount = () => {
    barcodeStore.setBarcodeCallback(this.onBarcodeScan)
  }

  componentWillUnmount = () => {
    barcodeStore.setBackToPrevBarcodeCallback()
  }

  onBarcodeScan = (cardId: string, batchNumber?: string, e?: KeyboardEvent) => {
    if (!(e?.target && e.target instanceof HTMLElement)) return
    if (!(e.target.id === this.CARD_ID) && e.target.id === this.PLACE_HOLDER_ID) return
    this.props.customer.setCardId(cardId)
    document.getElementById(this.CARD_ID)?.focus()
  }

  constructUndeleteWarningMessage = (errorMessage: string) => {
    return (
      errorMessage +
      ".\nChanges in the modal are lost when Un-delete Customer is selected.\nOnce the customer is undeleted, use the customer's email in the search bar to find the customer profile."
    )
  }

  updateUndeleteCustomerState = (error: any) => {
    const errorObj: CustomError = error?.response?.data
    if (!errorObj) return

    if (!errorObj.customErrorObject) return

    const isDeleted: boolean = errorObj.customErrorObject.customObject.isDeleted ?? false

    if (!isDeleted) {
      return
    }

    const undeleteId: string = errorObj.customErrorObject.customObject.id

    this.setState({
      errorMessage: this.constructUndeleteWarningMessage(this.state.errorMessage),
      customerIdToUndelete: undeleteId,
    })
  }

  save = async () => {
    if (!this.props.customer.name) {
      return this.setState({ errorMessage: 'Name is required to save' })
    }

    const createObject = toJS(this.props.customer)
    this.setState({ isSaving: true, errorMessage: '' })
    const isEditing = this.props.customer.id !== null
    const promise = isEditing
      ? this.props.currentCompanyStore!.editCustomer(this.props.customer.id!, createObject)
      : this.props.currentCompanyStore!.addCustomer(createObject)

    try {
      const customer = await promise
      openCustomerMFEModal({
        message: `Customer ${isEditing ? 'updated' : 'saved'} successfully`,
        severity: 'success',
      })
      this.props.onSubmit(customer)
    } catch (error) {
      const errorMessage =
        parseErrorMsg(error) ||
        `There was an error ${isEditing ? 'updating' : 'saving'} the customer`
      openCustomerMFEModal({
        message: errorMessage,
        severity: 'error',
      })
      this.setState({
        isSaving: false,
        errorMessage: errorMessage,
      })
      this.props.onCustomerUndelete && this.updateUndeleteCustomerState(error)
    }
  }

  undeleteCustomer = async () => {
    const { customerIdToUndelete } = this.state
    const { onCustomerUndelete } = this.props
    if (customerIdToUndelete && onCustomerUndelete) {
      try {
        await CustomersApi.patchCustomer(
          this.props.currentCompanyStore!.company!.id,
          customerIdToUndelete,
          {
            isDeleted: false,
          },
        )
        this.setState({
          isSaving: false,
          customerIdToUndelete: undefined,
        })
        onCustomerUndelete()
      } catch (error) {
        this.setState({
          isSaving: false,
          errorMessage: parseErrorMsg(error) || 'There was an error un-deleting the customer',
        })
      }
    }
  }

  get isValidBirthday() {
    const { customer } = this.props
    const birthday = customer.birthday ? moment(customer.birthday).format('MM/DD/YYYY') : ''
    return birthday.match(GrRegex.dateRegex)
  }

  get isPhoneValid() {
    const { customer } = this.props
    const phone = customer.phone || ''
    return isPhoneNumberValid(phone)
  }

  get isEmailAddressValid() {
    const { customer } = this.props
    const email = customer.email || ''
    return isEmailValid(email)
  }

  get isSaveButtonDisabled() {
    const { isSaving } = this.state
    const { canSaveCustomer } = this.props
    return (
      !canSaveCustomer ||
      isSaving ||
      !this.isPhoneValid ||
      !!this.isValidBirthday ||
      !this.isEmailAddressValid
    )
  }

  render() {
    const locations = this.props.currentCompanyStore!.locations || []
    const loyaltyTiers = this.props.currentCompanyStore!.loyaltyTiers || []
    const { customer, canSaveCustomer } = this.props
    const { showIdImageUploadModal, showProfileImageUploadModal, isSaving, customerIdToUndelete } =
      this.state

    let yearsOldSpan: JSX.Element | undefined
    if (customer.birthday) {
      const yearsOld = moment().diff(customer.birthday, 'years')
      yearsOldSpan = (
        <span className={yearsOld >= 19 ? 'text-success' : 'text-danger'}>
          ({yearsOld} years old)
        </span>
      )
    }

    let expiredSpan: JSX.Element | undefined
    if (customer.expiryDate) {
      if (moment().isSameOrAfter(customer.expiryDate, 'day')) {
        expiredSpan = <span className='text-danger'>Expired</span>
      } else {
        expiredSpan = <span className='text-success'>Valid</span>
      }
    }

    let buttonLabel: string
    if (isSaving) {
      buttonLabel = 'Saving...'
    } else if (!canSaveCustomer) {
      buttonLabel = 'No permission to save'
    } else {
      buttonLabel = 'Save'
    }

    return (
      <div className='customer-info-form'>
        <h5 className='text-secondary'>Notes</h5>
        <div className='custom-note form-group'>
          <textarea
            id={this.PLACE_HOLDER_ID}
            className='form-control'
            placeholder='Customer notes'
            value={customer.notes || undefined}
            onChange={(e) => customer.setNotes(e.target.value)}
          />
        </div>

        <h5 className='text-secondary info-title'>Details</h5>
        <div className='customer-details-container'>
          <div className='form-group'>
            <label className='control-label'>
              Name <span className='text-danger'>*</span>
            </label>
            <input
              id={this.PLACE_HOLDER_ID}
              className='form-control'
              value={customer.name}
              onChange={(e) => customer.setName(e.target.value)}
            />
          </div>
          <div className='form-group'>
            <label className='control-label'>Birthday {yearsOldSpan}</label>
            <Datepicker
              id={this.PLACE_HOLDER_ID}
              className={classNames('form-control', !!this.isValidBirthday && 'is-invalid')}
              peekNextMonth
              showMonthDropdown
              showYearDropdown
              dropdownMode='select'
              selected={customer.birthday ? moment(customer.birthday) : null}
              onChange={(date) => {
                const newBirthday = date ? date.toDate() : null
                customer.setBirthday(newBirthday)
              }}
            />
            {!!this.isValidBirthday && (
              <small className='text-danger'>Birthday must be a date</small>
            )}
          </div>
          <div className='form-group'>
            <label className='control-label'>Email</label>
            <input
              id={this.PLACE_HOLDER_ID}
              className={classNames('form-control', !this.isEmailAddressValid && 'is-invalid')}
              value={customer.email || ''}
              onChange={(e) => customer.setEmail(e.target.value)}
            />
            {!this.isEmailAddressValid && (
              <small className='text-danger'>Email must be a valid e-mail address</small>
            )}
          </div>
          <div className='form-group'>
            <label className='control-label'>Primary phone</label>
            <input
              id={this.PLACE_HOLDER_ID}
              className={classNames('form-control', !this.isPhoneValid && 'is-invalid')}
              type='tel'
              value={customer.phone || ''}
              onChange={(e) => {
                const numericPhone = GrRegex.cleanNotNumbers(e.target.value)
                customer.setPhone(numericPhone)
              }}
            />
            {!this.isPhoneValid && (
              <small className='text-danger'>Phone number should be 10-11 digits</small>
            )}
          </div>
          <div className='form-group'>
            <label className='control-label'>Gender</label>
            <Select
              id={this.PLACE_HOLDER_ID}
              value={customer.gender}
              placeholder='None selected'
              options={[
                {
                  value: Gender.MALE,
                  label: 'Male',
                },
                {
                  value: Gender.FEMALE,
                  label: 'Female',
                },
                {
                  value: Gender.OTHER,
                  label: 'Other',
                },
              ]}
              onChange={(option: { label: string; value: Gender }) => {
                customer.setGender(option ? option.value : null)
              }}
            />
          </div>
          <div className='form-group'>
            <label className='control-label'>Preferred Location</label>
            <Select
              id={this.PLACE_HOLDER_ID}
              value={this.customerLocationIdValue(customer)}
              placeholder='None selected'
              options={locations.map((location) => ({
                value: location.id,
                label: location.name,
              }))}
              onChange={(option: { label: string; value: number }) => {
                customer.setLocationId(option ? option.value : null)
              }}
            />
          </div>
        </div>

        <h5 className='text-secondary info-title'>Membership</h5>
        <div className='customer-details-container'>
          <div className='form-group'>
            <label className='control-label'>Card Id</label>
            <input
              id={this.CARD_ID}
              className='form-control'
              value={customer.cardId || undefined}
              onChange={(e) => customer.setCardId(e.target.value)}
            />
          </div>
          <div className='form-group'>
            <div>
              <label className='control-label mr-2'>Expiry date (optional) {expiredSpan}</label>
              <span className='fa fa-info-circle text-muted' data-tip data-for='expiry-date' />
              <Tooltip id='expiry-date' place='right' effect='solid'>
                <div>Leave blank if your memberships do not expire</div>
              </Tooltip>
            </div>
            <Datepicker
              id={this.PLACE_HOLDER_ID}
              className='form-control'
              peekNextMonth
              showMonthDropdown
              showYearDropdown
              dropdownMode='select'
              selected={customer.expiryDate ? moment(customer.expiryDate) : null}
              onChange={(date) => {
                const newExpiryDate = date ? date.toDate() : null
                customer.setExpiryDate(newExpiryDate)
              }}
            />
          </div>
          <div className='form-group'>
            <div>
              <label className='mr-2'>Loyalty tier</label>
              <span className='fa fa-info-circle text-muted' data-tip data-for='loyalty-tier' />
              <Tooltip id='loyalty-tier' place='right' effect='solid'>
                <div>
                  If your store has loyalty points, select the tier the customer should be assigned
                  to.
                </div>
                <div>If you have a default loyalty tier set, you can leave this field blank.</div>
              </Tooltip>
            </div>
            <Select
              id={this.PLACE_HOLDER_ID}
              value={customer.loyaltyTierId}
              placeholder='None selected'
              options={loyaltyTiers.map((loyaltyTier) => {
                return {
                  value: loyaltyTier.id,
                  label: loyaltyTier.name,
                }
              })}
              onChange={(option: { label: string; value: number }) => {
                customer.setLoyaltyTierId(option ? option.value : null)
              }}
            />
          </div>

          <div className='form-group'>
            <div>
              <label className='mr-2'>Tax exempt</label>
              <span className='fa fa-info-circle text-muted' data-tip data-for='tax-exempt' />
              <Tooltip id='tax-exempt' place='right' effect='solid'>
                <div>If your customer is tax-exempt, this customer will not be taxed</div>
              </Tooltip>
            </div>
            <input
              id={this.PLACE_HOLDER_ID}
              type='checkbox'
              checked={customer.isTaxExempt}
              onChange={() => customer.setIsTaxExempt(!customer.isTaxExempt)}
            />
          </div>
        </div>

        <h5 className='text-secondary info-title'>Address</h5>
        <div className='customer-details-container'>
          <div className='form-group'>
            <label className='control-label'>City</label>
            <input
              id={this.PLACE_HOLDER_ID}
              className='form-control'
              value={customer.city || ''}
              onChange={(e) => customer.setCity(e.target.value)}
            />
          </div>
          <div className='form-group'>
            <label className='control-label'>Address</label>
            <input
              id={this.PLACE_HOLDER_ID}
              className='form-control'
              value={customer.address || ''}
              onChange={(e) => customer.setAddress(e.target.value)}
            />
          </div>
          <div className='form-group'>
            <label className='control-label'>Province</label>
            <Select
              id={this.PLACE_HOLDER_ID}
              value={customer.province}
              options={[
                'BC',
                'AB',
                'SK',
                'MB',
                'ON',
                'QC',
                'NS',
                'NB',
                'PE',
                'NL',
                'YT',
                'NT',
                'NU',
              ].map((province) => {
                return {
                  label: province,
                  value: province,
                }
              })}
              onChange={(option: { label: string; value: Province }) => {
                customer.setProvince(option ? option.value : null)
              }}
            />
          </div>
          <div className='form-group'>
            <label className='control-label'>Postal code</label>
            <input
              id={this.PLACE_HOLDER_ID}
              className='form-control'
              value={customer.postalCode || ''}
              onChange={(e) => customer.setPostalCode(e.target.value)}
            />
          </div>
        </div>

        <h5 className='text-secondary info-title'>Identity</h5>
        <div className='customer-details-container'>
          <div className='form-group'>
            <label className='control-label'>ID Type</label>
            <Select
              id={this.PLACE_HOLDER_ID}
              value={customer.idType}
              placeholder='None selected'
              options={[
                {
                  value: 'Drivers License',
                  label: 'Drivers License',
                },
                {
                  value: 'Provincial ID',
                  label: 'Provincial ID',
                },
                {
                  value: 'Passport',
                  label: 'Passport',
                },
                {
                  value: 'Citizenship Card',
                  label: 'Citizenship Card',
                },
                {
                  value: 'Other Dispensary',
                  label: 'Other Dispensary',
                },
                {
                  value: 'Firearm License',
                  label: 'Firearm License',
                },
                {
                  value: 'Other',
                  label: 'Other',
                },
              ]}
              onChange={(option: { label: string; value: string }) => {
                customer.setIdType(option ? option.value : null)
              }}
            />
          </div>
          <div className='form-group'>
            <label className='control-label'>Identity card number</label>
            <input
              id={this.PLACE_HOLDER_ID}
              className='form-control'
              value={customer.driversLicenseNumber || ''}
              onChange={(e) => customer.setDriversLicenseNumber(e.target.value)}
            />
          </div>
        </div>

        <div className='bottom-section'>
          <div
            className='customer-id-container'
            onClick={() => {
              if (canSaveCustomer) {
                this.setState({ showProfileImageUploadModal: true })
              }
            }}
          >
            <h5 className='customer-id-title'>Profile Image</h5>
            {customer.secureProfileImageFileName ? (
              <SecureImage secureImageFileName={customer.secureProfileImageFileName} />
            ) : (
              <img src={require('../../images/user.png').default} />
            )}
            <button className='mt-3 image-upload-button'>
              <i className='fa fas fa-camera mr-3' />
              Upload profile image
            </button>
          </div>

          <div
            className='customer-id-container'
            onClick={() => {
              if (canSaveCustomer) {
                this.setState({ showIdImageUploadModal: true })
              }
            }}
          >
            <h5 className='customer-id-title'>Customer ID Image</h5>
            {customer.secureIdImageFileName ? (
              <SecureImage secureImageFileName={customer.secureIdImageFileName} />
            ) : (
              <img src={require('../../images/id-card.png').default} />
            )}
            <button className='mt-3 image-upload-button'>
              <i className='fa fas fa-camera mr-3' />
              Upload id image
            </button>
          </div>
        </div>

        <BlazeButton
          buttonProps={{
            className: classNames('w-100 mt-3'),
            type: 'primary',
            size: 'large',
            onClick: this.save,
            disabled: this.isSaveButtonDisabled,
          }}
          titleProps={{
            title: buttonLabel,
          }}
        />

        <div className='mt-3 white-space-pre-wrap'>
          <FormError message={this.state.errorMessage}>
            {customerIdToUndelete && (
              <div>
                <BlazeButton
                  buttonProps={{
                    onClick: this.undeleteCustomer,
                    className: 'ml-1',
                  }}
                  titleProps={{
                    title: 'Un-delete Customer',
                  }}
                />
              </div>
            )}
          </FormError>
        </div>

        {showProfileImageUploadModal && (
          <SecureImageUploadModal
            isOpen={true}
            onHide={() => this.setState({ showProfileImageUploadModal: false })}
            onUpload={(uploadObject) => {
              this.setState({
                showProfileImageUploadModal: false,
              })
              customer.setSecureProfileImage(uploadObject.fileName)
            }}
          />
        )}

        {showIdImageUploadModal && (
          <SecureImageUploadModal
            isOpen={true}
            onHide={() => this.setState({ showIdImageUploadModal: false })}
            onUpload={(uploadObject) => {
              this.setState({
                showIdImageUploadModal: false,
              })
              customer.setSecureIdImageFileName(uploadObject.fileName)
            }}
          />
        )}
      </div>
    )
  }
}
